import axios from 'axios';
import { createMutation, createQuery } from 'react-query-kit';

import { BaseValidation } from '@/components/datamodel/types';
import { appendEscapeParam, encodePathSegment, restUrl } from '@/services/utils';
import { Item, PersistedItem } from '@/types/DataModel';
import { ServerErrorResponse } from '@/types/ServerErrorResponse';

// System attributes that are allowed to be sent to the server when saving an item.
const ATTRIBUTE_WHITELIST = new Set(['primaryKey__', 'category__']);

export const sanitizeItemForSave = (item: Item): Item => {
    return Object.fromEntries(
        Object.entries(item).filter(([field]) => {
            return !field.endsWith('__') || ATTRIBUTE_WHITELIST.has(field);
        })
    ) as Item;
};

interface DefaultItemVariables {
    category: string;
}

const useDefaultItemQuery = createQuery<Item, DefaultItemVariables>({
    queryKey: ['defaultItem'],
    fetcher: async ({ category }) => {
        const response = await axios.get(restUrl(`items/defaultItem/${category}`));
        return response.data;
    }
});

interface ValidationResponse {
    attributeStates?: {
        [key: string]: ('hidden' | 'readonly')[];
    };
    calculations: {
        [key: string]: string | number;
    };
    validations: {
        [key:string]: BaseValidation[];
    }
}

interface ValidationVariables {
    // Specify the attribute names that the request should validate, where 'null' means validate the whole
    // `newItem` property.
    attributeNames: string[] | null;
    newItem: Item;
    oldItem: Item;
    attributeStatesOnly?: boolean;
}

const validate = async ({
    attributeNames = null,
    newItem,
    oldItem,
    attributeStatesOnly = false
}: ValidationVariables): Promise<ValidationResponse> => {
    const response = await axios.post<ValidationResponse>(restUrl('validate'), {
        attributeNames,
        newItem,
        oldItem,
        attributeStatesOnly
    });
    return response.data;
};

const useRemoteValidation = createMutation<ValidationResponse, ValidationVariables, ServerErrorResponse>({
    mutationFn: validate,
});

const useItem = createQuery<PersistedItem, { primaryKey__: string; }>({
    gcTime: 0,
    queryKey: ['item'],
    fetcher: async ({ primaryKey__ }) => {
        const esc = '~';
        const url = appendEscapeParam(
            restUrl(`items/${encodePathSegment(primaryKey__, esc)}?validate=true`),
            esc, primaryKey__
        );
        const response = await axios.get(url);
        return response.data;
    }
});

const useItems = createQuery<PersistedItem[], { primaryKeys: string[]; }>({
    gcTime: 0,
    queryKey: ['findByPrimaryKeys'],
    fetcher: async ({ primaryKeys }) => {
        const response = await axios.post(restUrl('items/findByPrimaryKeys'), {
            items: primaryKeys,
            fields: [],
            includeValidations: true
        });
        return response.data;
    }
});

interface ItemHierarchyElement {
    primaryKey: string;
    hierarchyName: string;
    childPrimaryKeys?: string[];
    parentPrimaryKeys?: string[];
    rootPrimaryKeys: string[];
    rootPaths?: string[][];
    root: boolean;
    childRelationValues?: { [childKey: string]: { [key: string]: any } };
    validation_warnings__?: any;
    validation__?: any;
    level?: number;
}

interface ItemHierarchy {
    rootPrimaryKey: string;
    hierarchyName: string;
    hierarchy: { [parentKey: string]: string[] };
    relationValues?: { [parent: string]: { [child: string]: { [key: string]: any } } };
}

const useItemHierarchy = createQuery<ItemHierarchyElement[], { primaryKey__: string; hierarchyName: string; }>({
    gcTime: 0,
    queryKey: ['itemHierarchies'],
    fetcher: async ({ primaryKey__ , hierarchyName}) => {
        const esc = '~';
        const url = appendEscapeParam(
            restUrl(`itemHierarchies/byItem/${encodePathSegment(primaryKey__, esc)}/singleBaseHierarchyElements/${hierarchyName}`),
            esc, primaryKey__
        );
        const response = await axios.get(url);
        return response.data;
    }
});

interface CopyItemVariables {
    primaryKey__: string;
    template: {[key: string]: any};
}

const useCopyItem = createMutation<PersistedItem, CopyItemVariables>({
    mutationFn: async ({ primaryKey__ , template}) => {
        const esc = '~';
        const url = appendEscapeParam(
            restUrl(`items/${encodePathSegment(primaryKey__, esc)}/copy?validate=true`),
            esc, primaryKey__
        );
        const response = await axios.post(url, { ...template });
        return response.data;
    }
});

interface CreateOrUpdateItemVariables {
    merge?: boolean;
    unique?: boolean;
    item: Item;
}

const useCreateOrUpdateItem =
    createMutation<PersistedItem, CreateOrUpdateItemVariables, ServerErrorResponse>({
        mutationFn: async ({ merge, unique, item }: CreateOrUpdateItemVariables) => {
            const sanitizedItem = sanitizeItemForSave(item);
            const response = await axios.post(restUrl(`items?merge=${merge}&unique=${unique}`), sanitizedItem);

            return response.data;
        }
    });

interface PreparedDefaultItemVariables {
    category__: string;
    template: {[key: string]: any};
}

/**
 * Convert the /validate `attributeStates` map into the `attributeStates__`
 * string-array format `initializeWithStates` already understands
 * (`"name:hidden"`, `"name:readonly"`).
 */
const flattenAttributeStatesMap = (
    attributeStates?: Record<string, string[]>,
): string[] => {
    if (!attributeStates) {
        return [];
    }
    return Object.entries(attributeStates).flatMap(([name, states]) =>
        (states ?? []).map((state) => `${name}:${state}`),
    );
};

/**
 * POST preparedDefaultItem (default-item script only), then a states-only
 * /validate so hidden/readonly are known before the editor paints.
 * Full field validation is kicked off asynchronously by the editor.
 * See LAX-10254.
 */
const fetchPreparedDefaultItem = async ({
    category__,
    template,
}: PreparedDefaultItemVariables): Promise<PersistedItem> => {
    const response = await axios.post(
        restUrl(`items/preparedDefaultItem/${category__}`),
        { ...template },
    );
    const item = response.data as PersistedItem;

    try {
        const statesResponse = await validate({
            attributeNames: null,
            newItem: sanitizeItemForSave(item),
            oldItem: {} as Item,
            attributeStatesOnly: true,
        });
        return {
            ...item,
            attributeStates__: flattenAttributeStatesMap(statesResponse.attributeStates),
        };
    } catch {
        // Still open the editor if the short states-only call fails; the
        // subsequent full validate will apply states when it returns.
        return {
            ...item,
            attributeStates__: item.attributeStates__ ?? [],
        };
    }
};

const usePreparedDefaultItem = createMutation<PersistedItem, PreparedDefaultItemVariables>({
    mutationFn: fetchPreparedDefaultItem,
});

export type { ItemHierarchy, ItemHierarchyElement };
export {
    fetchPreparedDefaultItem,
    flattenAttributeStatesMap,
    useCopyItem,
    useCreateOrUpdateItem,
    useDefaultItemQuery,
    useItem,
    useItemHierarchy,
    useItems,
    usePreparedDefaultItem,
    useRemoteValidation
};
